import { Eye, Pencil, Trash2, AlertTriangle, Clock, ArrowUp, ArrowDown, ChevronsUpDown, CheckCircle2, Lock } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import PriorityBadge from "@/components/PriorityBadge";
import EmailStatusBadge from "@/components/EmailStatusBadge";
import { getOverdueStatus, getTimeRemaining } from "@/utils/overdueUtils";
import { format } from "date-fns";

const SortableHead = ({ field, currentSort, currentDir, onSort, children, align = "left" }) => {
  const isActive = currentSort === field;
  const testId = `sort-header-${field}`;
  return (
    <TableHead
      className={`font-bold text-xs tracking-[0.2em] uppercase text-[#71717A] cursor-pointer select-none hover:text-[#09090B] transition-colors ${
        align === "right" ? "text-right" : ""
      }`}
      onClick={() => onSort(field)}
      data-testid={testId}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {children}
        {isActive ? (
          currentDir === "asc" ? (
            <ArrowUp className="w-3 h-3 text-[#09090B]" />
          ) : (
            <ArrowDown className="w-3 h-3 text-[#09090B]" />
          )
        ) : (
          <ChevronsUpDown className="w-3 h-3 opacity-40" />
        )}
      </span>
    </TableHead>
  );
};

const WorkOrderTable = ({ workOrders, loading, onView, onEdit, onDelete, sortBy, sortDir, onSort }) => {
  const { can } = useAuth();
  const canEdit = can("work_orders.edit");
  const canDelete = can("work_orders.delete");
  const formatDateTime = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-[#71717A]" data-testid="loading-state">
        Loading work orders...
      </div>
    );
  }

  if (workOrders.length === 0) {
    return (
      <div className="p-8 text-center text-[#71717A]" data-testid="empty-state">
        No work orders found. Create your first work order to get started.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#E4E4E7] hover:bg-transparent">
            <SortableHead field="wo_number" currentSort={sortBy} currentDir={sortDir} onSort={onSort}>WO Number</SortableHead>
            <TableHead className="font-bold text-xs tracking-[0.2em] uppercase text-[#71717A]">Site Name</TableHead>
            <TableHead className="font-bold text-xs tracking-[0.2em] uppercase text-[#71717A]">Engineer</TableHead>
            <TableHead className="font-bold text-xs tracking-[0.2em] uppercase text-[#71717A]">Description</TableHead>
            <SortableHead field="priority" currentSort={sortBy} currentDir={sortDir} onSort={onSort}>Priority</SortableHead>
            <TableHead className="font-bold text-xs tracking-[0.2em] uppercase text-[#71717A]">Billing</TableHead>
            <SortableHead field="created_at" currentSort={sortBy} currentDir={sortDir} onSort={onSort}>Start Date</SortableHead>
            <SortableHead field="end_datetime" currentSort={sortBy} currentDir={sortDir} onSort={onSort}>End Date</SortableHead>
            <SortableHead field="status" currentSort={sortBy} currentDir={sortDir} onSort={onSort}>Status</SortableHead>
            <TableHead className="font-bold text-xs tracking-[0.2em] uppercase text-[#71717A] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {workOrders.map((wo) => {
            const overdueStatus = getOverdueStatus(wo);
            const timeRemaining = getTimeRemaining(wo);
            const rowBg =
              overdueStatus === "overdue"
                ? "bg-[#FEF2F2] hover:bg-[#FEE2E2]"
                : overdueStatus === "approaching"
                ? "bg-[#FFF7ED] hover:bg-[#FFEDD5]"
                : "hover:bg-[#F4F4F5]";

            return (
              <TableRow
                key={wo.id}
                className={`border-[#E4E4E7] transition-colors duration-200 ${rowBg}`}
                data-testid={`work-order-row-${wo.work_order_number}`}
              >
                <TableCell className="font-semibold text-[#09090B]" data-testid={`wo-number-${wo.work_order_number}`}>
                  <div className="flex items-center gap-2">
                    {overdueStatus === "overdue" && (
                      <span title="Overdue" data-testid={`overdue-icon-${wo.work_order_number}`}>
                        <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
                      </span>
                    )}
                    {overdueStatus === "approaching" && (
                      <span title="Due soon" data-testid={`approaching-icon-${wo.work_order_number}`}>
                        <Clock className="w-4 h-4 text-[#EA580C]" />
                      </span>
                    )}
                    {wo.work_order_number}
                  </div>
                </TableCell>
                <TableCell className="text-[#09090B]">{wo.site_name}</TableCell>
                <TableCell className="text-[#09090B]" data-testid={`engineer-${wo.work_order_number}`}>
                  {wo.engineer_name ? (
                    <div className="flex flex-col gap-1">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-6 h-6 rounded-full bg-[#F4F4F5] border border-[#E4E4E7] flex items-center justify-center text-xs font-bold text-[#09090B]">
                          {wo.engineer_name.charAt(0).toUpperCase()}
                        </span>
                        {wo.engineer_name}
                      </span>
                      <EmailStatusBadge workOrder={wo} />
                    </div>
                  ) : (
                    <span className="text-[#71717A] text-sm italic">Unassigned</span>
                  )}
                </TableCell>
                <TableCell className="text-[#09090B] max-w-xs">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{wo.description}</span>
                    {wo.job_done_remark && wo.job_done_remark.trim() && (
                      <span
                        title={`Job Done: ${wo.job_done_remark}`}
                        className="flex-shrink-0"
                        data-testid={`remark-indicator-${wo.work_order_number}`}
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#059669]" />
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <PriorityBadge priority={wo.priority} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold border ${
                        wo.is_chargeable !== false
                          ? "bg-[#D1FAE5] text-[#059669] border-[#A7F3D0]"
                          : "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]"
                      }`}
                      data-testid={`billing-${wo.work_order_number}`}
                    >
                      {wo.is_chargeable !== false ? "Chargeable" : "Non-Chargeable"}
                    </span>
                    {wo.is_chargeable !== false && (
                      wo.amount != null && Number(wo.amount) > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase bg-[#DBEAFE] text-[#1D4ED8] border border-[#BFDBFE]"
                          data-testid={`billed-${wo.work_order_number}`}
                          title="Finance has entered the chargeable amount"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]" aria-hidden />
                          Billed
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.1em] uppercase bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]"
                          data-testid={`unbilled-${wo.work_order_number}`}
                          title="Finance has not entered an amount yet"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]" aria-hidden />
                          Not Billed
                        </span>
                      )
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-[#71717A] text-sm">{formatDateTime(wo.start_datetime)}</TableCell>
                <TableCell className="text-sm">
                  <div className="text-[#71717A]">{formatDateTime(wo.end_datetime)}</div>
                  {overdueStatus !== "none" && (
                    <div
                      className={`text-xs font-semibold mt-0.5 ${
                        overdueStatus === "overdue" ? "text-[#DC2626]" : "text-[#EA580C]"
                      }`}
                      data-testid={`time-remaining-${wo.work_order_number}`}
                    >
                      {timeRemaining}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <StatusBadge status={wo.status} isClosed={wo.is_closed} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onView(wo)}
                      className="rounded-sm hover:bg-[#F4F4F5]"
                      data-testid={`view-btn-${wo.work_order_number}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(wo)}
                      disabled={!canEdit}
                      title={!canEdit ? "No permission to edit" : undefined}
                      className="rounded-sm hover:bg-[#F4F4F5] disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`edit-btn-${wo.work_order_number}`}
                    >
                      {canEdit ? <Pencil className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(wo.id)}
                      disabled={!canDelete}
                      title={!canDelete ? "No permission to delete" : undefined}
                      className="rounded-sm hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`delete-btn-${wo.work_order_number}`}
                    >
                      {canDelete ? <Trash2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default WorkOrderTable;